import { useAccountContext } from '@seek/hirer-graphql-react';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  ButtonLink,
  ContentBlock,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';

import type { QueryParamTab } from 'src/hooks/useQueryParam';

import { useConfig } from '../../../hooks/useConfig';
import { createCAJALink } from '../JobActions/links';

import translations from './.vocab';
import { EmptySearchIcon } from './EmptySearchIcon';
import { ProfileIcon } from './ProfileIcon';

import { breakAnywhere } from 'src/App/styles/BreakAnywhere.css';

export interface EmptyStateProps {
  searchText?: string;
  tab: QueryParamTab;
}

export const EmptyState: React.FC<React.PropsWithChildren<EmptyStateProps>> = ({
  searchText,
  tab,
}) => {
  const { t } = useTranslations(translations);
  const { urlResolver } = useConfig();
  const { accountContext } = useAccountContext();
  const EmptySearchSection = () => {
    if (!searchText) {
      return null;
    }
    return (
      <Stack space="large" align="center">
        <EmptySearchIcon />
        <Box className={breakAnywhere}>
          <Heading level="2">
            {t('0 search results for', { searchText })}
          </Heading>
        </Box>
      </Stack>
    );
  };

  const cajaLink = urlResolver({ path: createCAJALink() });

  const EmptyJobsSection = () => (
    <Stack space="medium" align="center">
      <ProfileIcon />
      <Heading level="3">
        {tab === 'open' && t('You have no open jobs')}
        {tab === 'draft' && t('You have no drafts to edit')}
        {tab === 'expired' && t('You have no expired jobs')}
      </Heading>
      {tab !== 'expired' &&
        accountContext?.currentAdvertiser?.permissionOptions.canCreateJobs && (
          <>
            <Text tone="secondary">{t('Create your job ad now')}</Text>
            <Actions>
              <ButtonLink href={cajaLink} variant="ghost">
                {t('Create a job ad')}
              </ButtonLink>
            </Actions>
          </>
        )}
    </Stack>
  );

  return (
    <ContentBlock width="small">
      <Box textAlign="center">
        {searchText ? <EmptySearchSection /> : <EmptyJobsSection />}
      </Box>
    </ContentBlock>
  );
};
